import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Droppable } from 'react-beautiful-dnd';
import { List } from 'immutable';

import './stylesheet.css';

import * as baseActions from '../../actions/base';
import { requestNotificationPermission } from '../../lib/notifications';
import {
  areNotificationsSupported,
  DEFAULT_NOTIFICATION_SETTINGS,
  getNotificationsDisabledReason,
} from '../../lib/notification_utils';

import Switch from '../UI/Switch';
import LeadTimeRule from './components/LeadTimeRule';
import TagCadence from './components/TagCadence';

const NotificationsEditor = ({ notificationSettings, base }) => {
  const settings = notificationSettings || DEFAULT_NOTIFICATION_SETTINGS;
  const supported = areNotificationsSupported();
  const disabledReason = getNotificationsDisabledReason();
  const tagCadences = settings.get('tagCadences', List());

  const handleEnabledToggle = async () => {
    if (settings.get('enabled')) {
      base.updateNotificationSettingsFieldPathValue(['enabled'], false);
      return;
    }

    const permission = await requestNotificationPermission();
    if (permission === 'granted') {
      base.updateNotificationSettingsFieldPathValue(['enabled'], true);
    } else {
      alert(
        'Notifications permission was not granted. You can enable it later in your browser settings.'
      );
    }
  };

  const toggleRule = (fieldName) => () =>
    base.updateNotificationSettingsFieldPathValue(
      [fieldName, 'enabled'],
      !settings.getIn([fieldName, 'enabled'])
    );

  const updateRuleValue = (fieldName) => (event) =>
    base.updateNotificationSettingsFieldPathValue([fieldName, 'value'], event.target.value);

  const updateRuleUnit = (fieldName) => (newUnit) =>
    base.updateNotificationSettingsFieldPathValue([fieldName, 'unit'], newUnit);

  const handleAddNewCadenceClick = () => base.addNewEmptyNotificationTagCadence();

  const handleFieldPathUpdate = (cadenceId, fieldPath, newValue) =>
    base.updateNotificationTagCadenceFieldPathValue(cadenceId, fieldPath, newValue);

  const handleDeleteCadence = (cadenceId) => base.deleteNotificationTagCadence(cadenceId);

  if (!supported) {
    return (
      <div className="no-notifications-message">
        {disabledReason}
        <br />
        <br />
        Notifications use the Web Push API and are disabled in this browser.
      </div>
    );
  }

  return (
    <div className="notifications-editor-container">
      <div className="notifications-editor-defaults">
        <div className="notification-setting__field-container">
          <div className="notification-setting__field">
            <div>Enable notifications</div>
            <Switch isEnabled={!!settings.get('enabled')} onToggle={handleEnabledToggle} />
          </div>
          <div className="notification-setting__help-text">
            Reminders are scheduled locally from currently loaded Org files and shown by your
            browser. Keep organice open (or installed as an app) so they can fire. Enable "Sync on
            startup" in File settings for files you want reminders from.
          </div>
        </div>

        <LeadTimeRule
          label="Scheduled"
          helpText="Notify this long before a SCHEDULED timestamp."
          rule={settings.get('scheduled')}
          onToggle={toggleRule('scheduled')}
          onValueChange={updateRuleValue('scheduled')}
          onUnitChange={updateRuleUnit('scheduled')}
        />
        <LeadTimeRule
          label="Deadline"
          helpText="Notify this long before a DEADLINE timestamp."
          rule={settings.get('deadline')}
          onToggle={toggleRule('deadline')}
          onValueChange={updateRuleValue('deadline')}
          onUnitChange={updateRuleUnit('deadline')}
        />
        <LeadTimeRule
          label="Timestamp"
          helpText="Notify this long before an active timestamp in a heading title or description."
          rule={settings.get('timestamp')}
          onToggle={toggleRule('timestamp')}
          onValueChange={updateRuleValue('timestamp')}
          onUnitChange={updateRuleUnit('timestamp')}
        />
      </div>

      <div className="notifications-editor-section-title">Tag cadences</div>
      <div className="notification-setting__help-text" style={{ margin: '0 10px 10px' }}>
        Headings with a matching tag use that cadence instead of the default reminders above. Add
        more than one cadence for the same tag to get multiple reminders.
      </div>

      <Droppable droppableId="notification-tag-cadences-droppable" type="NOTIFICATION-TAG-CADENCE">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {tagCadences.size === 0 ? (
              <div className="no-notifications-message">
                You don't currently have any tag cadences - add one by pressing the{' '}
                <i className="fas fa-plus" /> button.
              </div>
            ) : (
              <Fragment>
                {tagCadences.map((cadence, index) => (
                  <TagCadence
                    key={cadence.get('id')}
                    index={index}
                    cadence={cadence}
                    onFieldPathUpdate={handleFieldPathUpdate}
                    onDeleteCadence={handleDeleteCadence}
                  />
                ))}
                {provided.placeholder}
              </Fragment>
            )}
          </div>
        )}
      </Droppable>

      <div className="new-capture-template-button-container">
        <button className="fas fa-plus fa-lg btn btn--circle" onClick={handleAddNewCadenceClick} />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  notificationSettings: state.base.get('notificationSettings'),
});

const mapDispatchToProps = (dispatch) => ({
  base: bindActionCreators(baseActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(NotificationsEditor);
